import React from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {
  FilterRowSelection,
  RelationalOperator,
} from '@lambda-layer/types/filter.ts';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';

export interface Props {
  filterRowSelection: FilterRowSelection;
  onChange: (filterRowValue: FilterRowSelection) => void;
  onFocusChange?: () => void;
  filterDefinition: FilterDefinition;
  autoFocus?: boolean;
}

const FilterRowBooleanToggle: React.FC<Props> = ({
  filterRowSelection,
  onChange,
  onFocusChange,
  filterDefinition,
  autoFocus = false,
}) => {
  const { label, defaultValue, defaultRelationalOperator } = filterDefinition;
  const isStringBasedBoolean =
    filterDefinition.databaseMetadata?.dataType === 'string';

  // Helper functions to handle both boolean and string-based boolean fields
  const getTrueValue = () => (isStringBasedBoolean ? '1' : true);

  // Determine current state
  const getCurrentState = () => {
    if (filterRowSelection.value.length === 0) {
      return 'disabled';
    }

    const hasDefaultValue = defaultValue?.[0] ?? getTrueValue();
    const hasDefaultOperator =
      defaultRelationalOperator ?? RelationalOperator.NOT_EQUAL;

    if (
      filterRowSelection.value[0] === hasDefaultValue &&
      filterRowSelection.relationalOperator === hasDefaultOperator
    ) {
      return 'exclude';
    } else if (
      filterRowSelection.value[0] === getTrueValue() &&
      filterRowSelection.relationalOperator === RelationalOperator.EQUAL
    ) {
      return 'showOnly';
    }

    return 'disabled';
  };

  const handleStateChange = (event: SelectChangeEvent<string>) => {
    const newState = event.target.value;

    switch (newState) {
      case 'exclude':
        // Exclude records (NOT EQUAL to "true" value)
        onChange({
          ...filterRowSelection,
          value: defaultValue ?? [getTrueValue()],
          relationalOperator:
            defaultRelationalOperator ?? RelationalOperator.NOT_EQUAL,
        });
        break;
      case 'showOnly':
        // Show only records (EQUAL to "true" value)
        onChange({
          ...filterRowSelection,
          value: [getTrueValue()],
          relationalOperator: RelationalOperator.EQUAL,
        });
        break;
      case 'disabled':
      default:
        // Disable filter (no filtering applied)
        onChange({
          ...filterRowSelection,
          value: [],
          relationalOperator: RelationalOperator.EQUAL,
        });
        break;
    }
  };

  // Create dynamic labels based on the field name
  const fieldName = label || 'Records';
  const excludeLabel = `Exclude ${fieldName}`;
  const showOnlyLabel = `Show Only ${fieldName}`;
  const disableLabel = 'No Filter';

  const currentState = getCurrentState();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
      <FormControl fullWidth size="small">
        <Select
          value={currentState}
          onChange={handleStateChange}
          onFocus={onFocusChange}
          onBlur={onFocusChange}
          autoFocus={autoFocus}
          displayEmpty
        >
          <MenuItem value="disabled">
            <Typography variant="body2" color="text.secondary">
              {disableLabel}
            </Typography>
          </MenuItem>
          <MenuItem value="exclude">
            <Typography variant="body2">{excludeLabel}</Typography>
          </MenuItem>
          <MenuItem value="showOnly">
            <Typography variant="body2">{showOnlyLabel}</Typography>
          </MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default FilterRowBooleanToggle;
